import * as THREE from 'three';
import type { WallDrop } from '@/sim/SimCore';
import { tuning } from '@/config/tuning';

/**
 * FreeKickWallView.ts — the FREE KICK WALL item's visual (M46): a three-man
 * defensive wall of glowing "defender" slabs (round head + body, arms crossed —
 * the classic free-kick silhouette) planted across the road where the item was
 * dropped. Purely cosmetic — SimCore owns the wall list + collisions; this view
 * mirrors it each frame, tinting each wall its owner's colour and fading it out
 * over its last second. Pooled: one reusable group per possible wall.
 */

const DEFENDERS = 3; // figures per wall (matches the "3-man wall" fantasy)
const BODY_H = 1.9;
const BODY_W = 1.15;
const HEAD_R = 0.36;
/** Seconds the wall takes to leap up when planted (M145). */
const PLANT_TIME = 0.28;

/** One pooled wall visual: a group of three defender figures + a base strip. */
class WallVisual {
  readonly group = new THREE.Group();
  readonly mat: THREE.MeshBasicMaterial;

  constructor() {
    this.mat = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.55,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    const body = new THREE.BoxGeometry(BODY_W, BODY_H, 0.5);
    const head = new THREE.SphereGeometry(HEAD_R, 10, 8);
    for (let i = 0; i < DEFENDERS; i++) {
      const x = (i - (DEFENDERS - 1) / 2) * (BODY_W + 0.5);
      const b = new THREE.Mesh(body, this.mat);
      b.position.set(x, BODY_H / 2, 0);
      const h = new THREE.Mesh(head, this.mat);
      h.position.set(x, BODY_H + HEAD_R * 0.8, 0);
      this.group.add(b, h);
    }
    // A faint ground strip marking the blocked span.
    const strip = new THREE.Mesh(new THREE.BoxGeometry(DEFENDERS * (BODY_W + 0.5) + 1, 0.08, 1.1), this.mat);
    strip.position.y = 0.04;
    this.group.add(strip);
    this.group.visible = false;
  }

  dispose(): void {
    this.mat.dispose();
    for (const c of this.group.children) (c as THREE.Mesh).geometry?.dispose();
  }
}

export class FreeKickWallView {
  readonly group = new THREE.Group();
  private pool: WallVisual[] = [];

  /** Mirror the sim's wall list (called each render frame). */
  update(walls: WallDrop[]): void {
    while (this.pool.length < walls.length) {
      const v = new WallVisual();
      this.pool.push(v);
      this.group.add(v.group);
    }
    for (let i = 0; i < this.pool.length; i++) {
      const v = this.pool[i];
      const w = walls[i];
      if (!w) {
        v.group.visible = false;
        continue;
      }
      v.group.visible = true;
      v.group.position.set(w.x, w.y, w.z);
      // Face the wall ACROSS the road: its local X spans wall-right. §6.1 — a
      // group fronting local +Z uses rotation.y = +heading of the track there.
      v.group.rotation.y = Math.atan2(w.dirX, w.dirZ);
      v.mat.color.set(w.color);
      // Fade out over the final second so despawn never pops.
      v.mat.opacity = 0.55 * Math.min(1, w.timer);
      // M145: the wall JUMPS up out of the road when it is planted (the three
      // defenders leaping to block), instead of just appearing. Derived from
      // the wall's own clock, so it needs no state and matches online.
      const k = Math.min(1, (tuning.powerups.wall.life - w.timer) / PLANT_TIME);
      const rise = k * (1 + 0.4 * Math.sin(k * Math.PI)); // overshoot, then settle
      v.group.scale.set(0.7 + 0.3 * k, Math.max(0.02, rise), 1);
    }
  }

  hide(): void {
    for (const v of this.pool) v.group.visible = false;
  }

  dispose(): void {
    for (const v of this.pool) v.dispose();
    this.pool = [];
  }
}
